import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Copy, CheckCircle, Gift, Users, Loader2, Share2 } from 'lucide-react';
import { logger } from '../utils/logger';
import { API } from '../utils/apiBase';


export default function ReferralProgram() {
  const navigate = useNavigate();
  const apiRef = useRef(API);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyCode, setApplyCode] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState('');
  const [applyError, setApplyError] = useState('');
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${apiRef.current}/referral/info`, { withCredentials: true });
        setInfo(res.data);
      } catch (err) {
        logger.error('Failed to fetch referral info:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, []);

  const copyText = useCallback((text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  }, []);

  const handleApply = useCallback(async () => {
    if (!applyCode.trim()) return;
    setApplying(true);
    setApplyMsg('');
    setApplyError('');
    try {
      const res = await axios.post(`${apiRef.current}/referral/apply`, { code: applyCode.trim() }, { withCredentials: true });
      setApplyMsg(res.data.message);
      setApplyCode('');
    } catch (err) {
      setApplyError(err.response?.data?.detail || 'Failed to apply referral code.');
      logger.error('Referral apply failed:', err);
    } finally {
      setApplying(false);
    }
  }, [applyCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] pt-20 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#002fa7]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20" data-testid="referral-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/dashboard')} className="inline-flex items-center space-x-2 text-[#4b5563] hover:text-[#002fa7] mb-4" data-testid="back-button">
          <ArrowLeft size={20} /><span>Back to Dashboard</span>
        </button>
        <h1 className="font-satoshi text-3xl font-black tracking-tight text-[#0f1115] mb-1">Referral Program</h1>
        <p className="text-[#4b5563] mb-8">Invite friends and both earn bonus credits</p>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="dashboard-card p-6 text-center" data-testid="stat-referrals">
            <Users size={24} className="mx-auto text-[#002fa7] mb-2" />
            <p className="text-3xl font-black text-[#0f1115]">{info?.referral_count || 0}</p>
            <p className="text-xs uppercase tracking-wider text-[#6b7280] font-semibold mt-1">Friends Referred</p>
          </div>
          <div className="dashboard-card p-6 text-center" data-testid="stat-credits">
            <Gift size={24} className="mx-auto text-[#10b981] mb-2" />
            <p className="text-3xl font-black text-[#0f1115]">{info?.credits_earned || 0}</p>
            <p className="text-xs uppercase tracking-wider text-[#6b7280] font-semibold mt-1">Credits Earned</p>
          </div>
          <div className="dashboard-card p-6 text-center" data-testid="stat-code">
            <Share2 size={24} className="mx-auto text-[#eab308] mb-2" />
            <p className="text-lg font-black text-[#0f1115] font-mono tracking-widest">{info?.code || '—'}</p>
            <p className="text-xs uppercase tracking-wider text-[#6b7280] font-semibold mt-1">Your Code</p>
          </div>
        </div>

        {/* Share Section */}
        <div className="dashboard-card p-6 mb-6" data-testid="share-section">
          <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-4">Share Your Referral Link</h2>

          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-[#6b7280] block mb-1">Referral Code</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={info?.code || ''}
                  className="flex-1 px-4 py-3 border border-[#e5e7eb] bg-[#f4f5f7] text-sm font-mono"
                  data-testid="referral-code-input"
                />
                <button
                  onClick={() => copyText(info?.code, 'code')}
                  className="btn-outline text-sm inline-flex items-center gap-1 whitespace-nowrap"
                  data-testid="copy-code-btn"
                >
                  {copiedField === 'code' ? <><CheckCircle size={14} className="text-[#10b981]" /> Copied</> : <><Copy size={14} /> Copy</>}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-[#6b7280] block mb-1">Referral Link</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={info?.referral_link || ''}
                  className="flex-1 px-4 py-3 border border-[#e5e7eb] bg-[#f4f5f7] text-sm truncate"
                  data-testid="referral-link-input"
                />
                <button
                  onClick={() => copyText(info?.referral_link, 'link')}
                  className="btn-outline text-sm inline-flex items-center gap-1 whitespace-nowrap"
                  data-testid="copy-link-btn"
                >
                  {copiedField === 'link' ? <><CheckCircle size={14} className="text-[#10b981]" /> Copied</> : <><Copy size={14} /> Copy</>}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Apply Code Section */}
        <div className="dashboard-card p-6 mb-6" data-testid="apply-section">
          <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-4">Have a Referral Code?</h2>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={applyCode}
              onChange={(e) => setApplyCode(e.target.value)}
              placeholder="Enter referral code..."
              className="flex-1 px-4 py-3 border border-[#e5e7eb] text-sm focus:border-[#002fa7] transition-colors uppercase"
              data-testid="apply-code-input"
            />
            <button
              onClick={handleApply}
              disabled={applying || !applyCode.trim()}
              className="btn-primary flex items-center gap-2 disabled:opacity-50 whitespace-nowrap"
              data-testid="apply-btn"
            >
              {applying ? <Loader2 size={16} className="animate-spin" /> : 'Apply Code'}
            </button>
          </div>
          {applyMsg && <p className="text-sm text-[#10b981] mt-2" data-testid="apply-success">{applyMsg}</p>}
          {applyError && <p className="text-sm text-[#e11d48] mt-2" data-testid="apply-error">{applyError}</p>}
        </div>

        {/* How It Works */}
        <div className="dashboard-card p-6" data-testid="how-it-works">
          <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-4">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-10 h-10 bg-[#002fa7] text-white flex items-center justify-center font-bold mx-auto mb-3">1</div>
              <p className="text-sm font-semibold text-[#0f1115]">Share your link</p>
              <p className="text-xs text-[#6b7280] mt-1">Send your referral link to friends who are job searching</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 bg-[#002fa7] text-white flex items-center justify-center font-bold mx-auto mb-3">2</div>
              <p className="text-sm font-semibold text-[#0f1115]">They sign up</p>
              <p className="text-xs text-[#6b7280] mt-1">Your friend creates an account using your link</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 bg-[#002fa7] text-white flex items-center justify-center font-bold mx-auto mb-3">3</div>
              <p className="text-sm font-semibold text-[#0f1115]">Both earn credits</p>
              <p className="text-xs text-[#6b7280] mt-1">You and your friend each get 1 bonus credit</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
